import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  AlertOctagon, Bus, ChevronLeft, ChevronRight, Eye, Play, Radio,
} from 'lucide-react'
import { useSession } from '../../auth/useSession'
import { clock } from '../../lib/format'
import { pollWhileVisible } from '../../lib/pollWhileVisible'
import { EmptyState } from '../../cc/EmptyState'
import { FilterChips } from '../../cc/FilterChips'
import { KpiCard } from '../../cc/KpiCard'
import { KpiStrip } from '../../cc/KpiStrip'
import { Panel } from '../../cc/Panel'
import { SearchBox } from '../../cc/SearchBox'
import { SortBar } from '../../cc/SortBar'
import { EYEBROW, PANEL } from '../../cc/tokens'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, type FleetId } from '../../cc/fleetNav'
import { TopBar } from '../../cc/TopBar'
import { VehicleCard, type VehicleStatus } from '../../cc/VehicleCard'
import {
  isAlertVisible, isSustainedAlertStillOpen, isSustainedConditionKind, useSustainedAlertDeadSet,
} from '../../lib/alertVisibility'
import { TuplLiveMap } from './TuplLiveMap'
import { TuplEvidence } from './TuplEvidence'
import { TuplDutyPlayback } from './TuplDutyPlayback'
import { useTuplDaySummary } from './useTuplDaySummary'
import { useTuplStatusEvents } from './useTuplStatusEvents'
import { TuplBreakdownForm } from './TuplBreakdownForm'
import { TuplBoard } from './TuplBoard'
import { TuplTripAlerts } from './TuplTripAlerts'
import { TuplVehicleTimelineModal } from './TuplVehicleTimelineModal'
import {
  fetchTuplActionables,
  fetchTuplBoard,
  fetchTuplBreakdowns,
  fetchTuplCodes,
  fetchTuplDuties,
  fetchTuplEta,
  fetchTuplGeotags,
  fetchTuplLive,
  fetchTuplReplay,
  fetchTuplRouteStops,
  fetchTuplRoutes,
  fetchTuplTrack,
  fetchTuplTripAlerts,
  fetchTuplVehicleDayAlerts,
  fetchTuplTripLog,
  fetchTuplVehicleEfficiency,
  hasReachedStop,
  inferDirectionFromGeotags,
  tuplDutyFor,
  nextRouteStopFromGeotags,
  normalizeRunDirection,
  istDateOf,
  todayIst,
  type TuplActionable,
  type TuplBoardRow,
  type TuplBreakdown,
  type TuplCode,
  type TuplDutyClip,
  type TuplGeotagEvent,
  type TuplReplayClip,
  type TuplRoute,
  type TuplRouteEta,
  type TuplRouteStop,
  type TuplStage,
  type TuplTripLogRow,
  type TuplVehicle,
  type TuplVehicleEfficiency,
} from './useTuplApi'

function sessionDisplayName(session: ReturnType<typeof useSession>): string | undefined {
  return session.status === 'signed-in' ? session.session.name || session.session.email : undefined
}

function ago(ms: number | null | undefined): string {
  if (ms == null) return '—'
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h`
}

/** openEvidence()'s day-alerts fetch window, counting back from the clip's own end --
 *  round-3 adversarial-review finding: capping the DAY LIST (an earlier approach,
 *  MAX_DAYS=10) truncated in the MIDDLE for a long-open breakdown, dropping an arbitrary
 *  interior stretch with no indication, while still firing up to 11 parallel requests.
 *  Clamping the WINDOW instead means any truncation is always at the OLD end (the days
 *  an operator cares about least) and the result is always a handful of requests, never
 *  more. Exported so tests can derive the clamp boundary from this value instead of
 *  hardcoding it. */
export const TUPL_EVIDENCE_ALERTS_WINDOW_MS = 2 * 86_400_000

/** Calendar-day distance between two IST date strings ('YYYY-MM-DD'), as a count of
 *  whole days -- 0 for the same day, 1 for adjacent days, etc. Parsed as UTC midnight
 *  purely as an arithmetic anchor for diffing date LABELS, not real instants; the inputs
 *  are already IST calendar dates (istDateOf's own output), so no timezone conversion
 *  happens here. */
function istCalendarDayGap(dateA: string, dateB: string): number {
  return Math.round((Date.parse(`${dateB}T00:00:00Z`) - Date.parse(`${dateA}T00:00:00Z`)) / 86_400_000)
}

/** Every distinct IST calendar date openEvidence() needs a day-alerts request for: the
 *  most recent TUPL_EVIDENCE_ALERTS_WINDOW_MS of the clip's own window, PLUS -- always,
 *  unconditionally -- the clip's own onset day (`istDateOf(startMs)`). The onset day is
 *  not optional: for a breakdown open longer than the clamp window, the old end of that
 *  window IS the incident's own reported/lead-in start (serving/tupl.py's
 *  `started_ts = opened_ts - LEAD_IN_MS`), so clamping without it would drop the exact
 *  alert that triggered the breakdown in the first place -- the whole reason the modal
 *  was opened (round-4 adversarial-review finding).
 *
 *  `truncated` is computed HERE, not reconstructed by the caller from `dates[0]` --
 *  round-5 adversarial-review finding: comparing `istDateOf(startMs)` against the first
 *  emitted date false-positived whenever the clamped window's own start lands on the
 *  calendar day immediately AFTER onset (which happens for roughly half of all 2-3-day
 *  incidents depending on time-of-day) -- onset and the clamped window are ADJACENT in
 *  that case, so nothing was actually skipped, but the naive comparison still read
 *  "different date" as "truncated". The real question is a calendar-day GAP, not a
 *  string inequality: truncated only when a whole day sits strictly between the onset
 *  and the earliest day the clamped window itself covers, with no request for it. */
function istDatesBetween(startMs: number, endMs: number): { dates: string[]; truncated: boolean } {
  // Guard against start > end (a clock-skew bug elsewhere) -- without this the loop below
  // never runs and `dates[0]` ends up in the wrong order, breaking every invariant a
  // caller might rely on.
  const s = Math.min(startMs, endMs)
  const e = Math.max(startMs, endMs)
  const clampedStart = Math.max(s, e - TUPL_EVIDENCE_ALERTS_WINDOW_MS)
  const dates: string[] = []
  const seen = new Set<string>()
  for (let t = clampedStart; t <= e; t += 86_400_000) {
    const d = istDateOf(t)
    if (!seen.has(d)) { seen.add(d); dates.push(d) }
  }
  const endDate = istDateOf(e)
  if (!seen.has(endDate)) { seen.add(endDate); dates.push(endDate) }
  const onsetDate = istDateOf(s)
  const truncated = !seen.has(onsetDate)
    && istCalendarDayGap(onsetDate, dates[0] ?? endDate) > 1
  if (!seen.has(onsetDate)) { seen.add(onsetDate); dates.push(onsetDate) }
  return { dates, truncated }
}

// Same reasoning and same value as BusDepotBoard.tsx's own LIVE_EVENT_STALE_MS:
// useTuplStatusEvents' `live` map accumulates one entry per vehicle and never expires
// it on its own, so a vehicle that sent one frame and then went dark would otherwise
// show that frame's position/speed forever rather than falling back to the poll's own
// (correctly on_air=false) row.
const TUPL_LIVE_EVENT_STALE_MS = 5 * 60_000

/**
 * TUPL live command centre. OpenStreetMap over the Ulhasnagar depot.
 */
export function TuplOpsShell(): JSX.Element {
  const navigate = useNavigate()
  const { tab } = useParams()
  // 'alerts' kept routable (not just 'trip-alerts') for URL continuity -- anyone with
  // /bus-ops/alerts bookmarked from before the two alert tabs merged still lands on the
  // one surface, same precedent as Phase 1's /bus-ops/intel graceful fallthrough.
  const view = tab === 'board' ? 'board'
    : (tab === 'alerts' || tab === 'trip-alerts') ? 'trip-alerts' : 'live'
  const session = useSession()
  const userLabel = sessionDisplayName(session)

  const daySummary = useTuplDaySummary()
  const liveStatus = useTuplStatusEvents()
  const sustainedAlertDeadSet = useSustainedAlertDeadSet()
  const [now, setNow] = useState(() => Date.now())
  const [vehicles, setVehicles] = useState<TuplVehicle[]>([])
  const [routes, setRoutes] = useState<TuplRoute[]>([])
  const [stages, setStages] = useState<TuplStage[]>([])
  const [onAir, setOnAir] = useState(0)
  const [moving, setMoving] = useState(0)
  const [register, setRegister] = useState(0)
  const [actionables, setActionables] = useState<TuplActionable[]>([])
  const [todayAlertCount, setTodayAlertCount] = useState(0)
  const [breakdowns, setBreakdowns] = useState<TuplBreakdown[]>([])
  const [resolved, setResolved] = useState<TuplBreakdown[]>([])
  const [codes, setCodes] = useState<TuplCode[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  // Read inside pointAtVehicle below without putting `selected` in that callback's own
  // deps (which would recreate it, and every caller that depends on its identity, on
  // every selection -- the callback only needs to compare against whatever was selected
  // at CLICK time, not re-render for it).
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  // Bumped on every fleet/alert click so re-selecting the same plate still pans the map.
  const [focusTick, setFocusTick] = useState(0)
  // Today's total matching trip-alert rows (SPEC.md AC-1: the chip's count is this
  // surface's own fetch, not the differently-scoped live actionables list). Read here at
  // shell level, not from TuplTripAlerts itself -- an earlier version lifted this via a
  // prop callback from that component, but it is only mounted while `view ===
  // 'trip-alerts'`, so the chip read a stale "0" on every other view (adversarial review
  // caught this). Fetched inside refresh() below, not its own interval -- CLAUDE.md's
  // live-data section is explicit that a page gets at most one heal-poll, and this was a
  // second, independent 15s loop stacked on top of it.
  const [tripAlertsTotal, setTripAlertsTotal] = useState(0)
  const [board, setBoard] = useState<TuplBoardRow[]>([])
  const [boardErr, setBoardErr] = useState<string | null>(null)
  const [boardLoaded, setBoardLoaded] = useState(false)
  const [timelineVehicleId, setTimelineVehicleId] = useState<string | null>(null)
  const [timelineRows, setTimelineRows] = useState<TuplTripLogRow[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [efficiency, setEfficiency] = useState<TuplVehicleEfficiency | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  // Fleet lives in a persistent left drawer, not a queue section or a nav tab -- present
  // across every view (live/board/alerts/trip-alerts), collapsible for map space, open by
  // default so it's never a click away.
  const [fleetOpen, setFleetOpen] = useState(true)
  // Breakdown evidence clip -- only ever set by an explicit "View evidence" click
  // (openEvidence below), never by selection alone (that auto-overlay onto the shared live
  // map was the bug: clicking an alert tile used to replace the live vehicle marker with a
  // replay trail instead of showing its current state). Kept named `clip`/`setClip` (not
  // renamed) so the pre-existing AC-E3 "no Google ETA during playback" guard
  // (`if (!selected || duty || clip || selectedIsDown)` below) keeps reading as the
  // same rule it always was: pause ETA/geotag chatter while any playback modal is open --
  // the `selectedIsDown` half is new (round-3/4 adversarial-review findings): the OLD
  // auto-fetch effect used to populate `clip` the instant a selected bus had an open
  // breakdown, which incidentally ALSO gated this guard off for broken-down buses; once
  // that auto-fetch was removed, `clip` no longer does that, and the ETA chain started
  // firing (and billing Google) for a vehicle that is physically stopped. Checking
  // `selectedIsDown` (a primitive derived from `down.has(selected)`, not `down` itself --
  // see its own declaration below for why) directly restores that behavior without
  // reviving the auto-fetch.
  const [clip, setClip] = useState<TuplReplayClip | null>(null)
  // That vehicle's day alerts, filtered to the clip's own window -- see openEvidence.
  const [clipAlerts, setClipAlerts] = useState<TuplActionable[]>([])
  // True when the clip's own window is longer than TUPL_EVIDENCE_ALERTS_WINDOW_MS, so
  // clipAlerts only covers the most recent slice of it -- surfaced in the modal rather
  // than silently showing an undercount with no indication (round-3 adversarial-review
  // finding).
  const [clipAlertsTruncated, setClipAlertsTruncated] = useState(false)
  const [duty, setDuty] = useState<TuplDutyClip | null>(null)
  const [dutyLoading, setDutyLoading] = useState(false)
  const [evidenceLoading, setEvidenceLoading] = useState(false)
  // "Last call wins" guard for playDuty() -- same shape as TuplTripAlerts.tsx's own
  // `loadId`. Without it, clicking Play Duty for vehicle A then quickly for vehicle B
  // could have A's slower Promise.all resolve after B's and clobber the already-open
  // duty back to the wrong plate (found on adversarial review: SPEC.md claimed this
  // guard already existed here, but it did not).
  const dutyReqRef = useRef(0)
  // Same "last call wins" guard for openEvidence() below.
  const evidenceReqRef = useRef(0)
  const vehiclesRef = useRef(vehicles)
  vehiclesRef.current = vehicles
  const [breakdownFormFor, setBreakdownFormFor] = useState<string | null>(null)

  const pointAtVehicle = useCallback((id: string) => {
    // Switching to a DIFFERENT vehicle invalidates any in-flight playDuty()/
    // openEvidence() fetch for the previous one -- round-3 adversarial-review finding,
    // a regression this ticket introduced: the deleted auto-fetch effect used to be keyed
    // on openBd's breakdown_id and duty together, so selecting a different vehicle (which changes
    // `openBd`) implicitly cancelled it via `setClip(null)`. Without an equivalent here,
    // selecting vehicle A, clicking "View evidence" for it, then selecting vehicle B
    // before A's fetch resolved let A's evidence modal pop open while B was selected, and
    // left B's own "View evidence"/"Play duty" button stuck on A's loading flag. Guarded
    // on an actual change (not re-selecting the SAME plate) so this never cancels the
    // very fetch a click just started for that plate.
    if (id !== selectedRef.current) {
      ++dutyReqRef.current
      ++evidenceReqRef.current
      setDutyLoading(false)
      setEvidenceLoading(false)
      setDuty(null)
      setClip(null)
      setClipAlerts([])
      setClipAlertsTruncated(false)
    }
    setSelected(id)
    setFocusTick((n) => n + 1)
    if (view !== 'live') navigate('/bus-ops')
  }, [navigate, view])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Codes and routes/stages are config-like -- a breakdown category catalogue and the
  // route network, not something that changes mid-session. CLAUDE.md's live-data section
  // (added after this exact poll loop was found running 6 endpoints every 2.5s, confirmed
  // live 2026-10-23) is explicit that data at this timescale is fetched once per page
  // load, never re-polled. Pulled out of refresh() below for that reason -- they used to
  // be two of the six requests firing every 2.5s for no reason, since neither value had
  // ever actually changed between one tick and the next in the entire time this ran.
  useEffect(() => {
    let live = true
    fetchTuplCodes().then((c) => { if (live) setCodes(c) }).catch(() => {})
    fetchTuplRoutes()
      .then((net) => { if (live) { setRoutes(net.routes); setStages(net.stages) } })
      .catch(() => {})
    return () => { live = false }
  }, [])

  // Guards against the 8s interval below firing a fresh round of parallel fetches on top
  // of a round that is still in flight. Without this, one slow server response (GC pause,
  // cold cache, a brief backend blip) means every subsequent tick adds more requests
  // behind it rather than replacing it -- an unbounded, self-inflicted backlog that
  // saturates the browser's per-origin connection limit and piles concurrent load onto
  // the backend, observed live: 70+ requests stuck pending within 10 seconds, the
  // server's own /api/state answering 503 under the resulting load.
  //
  // THIS IS A HEAL-POLL, not the live feed itself -- same shape and same 8s interval as
  // useFleetData.ts's own roster-sync poll, per CLAUDE.md's live-data section. Vehicle
  // position/speed/heading/soc/moving already arrive over SSE (useTuplStatusEvents,
  // merged in below); this poll's live() call exists only to catch what SSE structurally
  // cannot push -- a vehicle going silent produces no frame, so on_air/seen_s transitions
  // are only ever visible here. actionables/breakdowns/trip-alerts total have no SSE push
  // of their own yet (follow-up), so they ride this same poll rather than a second one
  // of their own.
  const refreshInFlightRef = useRef(false)

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return
    refreshInFlightRef.current = true
    // All 5 fire immediately, same as the single Promise.all this replaces (the overlap
    // guard's own call-count tests depend on that) -- only the awaiting/error-handling is
    // split into isolated groups, so one call failing (e.g. breakdowns) can't hold back
    // the live vehicle feed that resolved fine. Mirrors NagpurTuplOpsShell's refresh().
    const livePromise = fetchTuplLive()
    const actionablesPromise = fetchTuplActionables()
    const openBreakdownsPromise = fetchTuplBreakdowns(true)
    const resolvedBreakdownsPromise = fetchTuplBreakdowns(false)
    const tripAlertsPromise = fetchTuplTripAlerts({ dateFrom: todayIst(), dateTo: todayIst(), hasAlerts: true, limit: 1 })
    try {
      const live = await livePromise
      setVehicles(live.vehicles)
      setOnAir(live.on_air)
      setMoving(live.moving)
      setRegister(live.register)
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'live feed failed')
    }
    try {
      const a = await actionablesPromise
      setActionables(a.items)
      setTodayAlertCount(a.todayAlertCount)
    } catch {
      // A failed poll should not blank the previously-loaded actionables queue.
    }
    try {
      setBreakdowns(await openBreakdownsPromise)
    } catch {
      // Same reasoning -- a stale breakdown list beats none at all.
    }
    try {
      const r = await resolvedBreakdownsPromise
      setResolved(r.filter((row) => row.cleared_ts != null).slice(0, 12))
    } catch {
      // Same reasoning.
    }
    try {
      setTripAlertsTotal((await tripAlertsPromise).total)
    } catch {
      // A stale count beats none at all.
    }
    setLoaded(true)
    refreshInFlightRef.current = false
  }, [])

  useEffect(() => pollWhileVisible(() => void refresh(), 8000), [refresh])

  const loadBoard = useCallback(async () => {
    try {
      const b = await fetchTuplBoard()
      setBoard(b.rows)
      setBoardErr(b.duties_error)
    } catch (e) {
      setBoardErr(e instanceof Error ? e.message : 'board fetch failed')
    } finally {
      setBoardLoaded(true)
    }
  }, [])

  useEffect(() => { void loadBoard() }, [loadBoard])

  useEffect(() => {
    if (view !== 'board') return
    return pollWhileVisible(() => void loadBoard(), 15_000)
  }, [view, loadBoard])

  // Same endpoint the Duty log report reads, filtered to this one plate and today's IST
  // date -- so the board's timeline can never drift out of sync with what the trip log
  // itself shows for that vehicle.
  useEffect(() => {
    if (!timelineVehicleId) { setTimelineRows([]); return }
    let live = true
    setTimelineLoading(true)
    // todayIst(), not new Date().toISOString().slice(0, 10) -- the exact bug that
    // helper exists to fix (see its own comment): a UTC-today default reads as
    // yesterday for the first 5.5 hours of every IST day, so clicking a board row
    // during that window fetched an empty trip log for a vehicle that has already
    // been running for hours.
    const today = todayIst()
    fetchTuplTripLog(500, today, today, timelineVehicleId)
      .then((rows) => { if (live) setTimelineRows(rows) })
      .catch(() => { if (live) setTimelineRows([]) })
      .finally(() => { if (live) setTimelineLoading(false) })
    return () => { live = false }
  }, [timelineVehicleId])

  useEffect(() => {
    if (!selected) { setEfficiency(null); return }
    let live = true
    fetchTuplVehicleEfficiency(selected)
      .then((e) => { if (live) setEfficiency(e) })
      .catch(() => { if (live) setEfficiency(null) })
    return () => { live = false }
  }, [selected])

  const [selectedGeotags, setSelectedGeotags] = useState<TuplGeotagEvent[]>([])
  const [selectedEta, setSelectedEta] = useState<TuplRouteEta | null>(null)
  const [nextStopLabel, setNextStopLabel] = useState<string | null>(null)
  // The next stop's own coordinates, alongside its label -- so arrival can be checked
  // against the live poll (every few seconds) instead of only at the moment the Google
  // ETA itself was fetched (every 45s). See hasArrivedAtNextStop below.
  const [nextStopPos, setNextStopPos] = useState<{ lat: number; lon: number } | null>(null)
  // Up/Down for map beads + next-stop: geotag history wins over sticky GPS nearest-stop.
  const [runDirection, setRunDirection] = useState<'up' | 'down' | undefined>(undefined)
  // Both legs for the selected route, fetched once per selection by loadNextAndEta below
  // and reused by the fast resync effect so that effect never needs its own fetch.
  const [allStopsForSelected, setAllStopsForSelected] = useState<TuplRouteStop[]>([])

  const down = useMemo(() => new Set(breakdowns.map((b) => b.vehicle_id)), [breakdowns])
  // A primitive, not `down` itself, in the ETA/geotag effects' own dep arrays below --
  // `down` is a new Set identity every ~8s poll tick (breakdowns gets a new array every
  // tick regardless of content), and putting the Set itself in deps would re-subscribe
  // (and immediately re-fire) the 45s Google-costing poll on that same ~8s cadence. This
  // boolean only changes VALUE on a real down/up transition for the selected plate, so
  // the effects below only re-run (and, critically, actually take their clearing branch)
  // when that transition genuinely happens -- round-4 adversarial-review finding: a
  // `downRef` read only inside the poll's own async callback made the fetch stop, but
  // never re-ran the effect itself, so the clearing branch (which resets
  // selectedEta/nextStopLabel/runDirection) never fired, leaving a stale ETA/next-stop
  // readout on screen forever once a selected bus went down.
  const selectedIsDown = selected != null && down.has(selected)
  const openBd = breakdowns.find((b) => b.vehicle_id === selected)
    ?? resolved.find((b) => b.vehicle_id === selected && selected != null)
  const bus = vehicles.find((v) => v.vehicle_id === selected)
  // Direct instruction: the ETA panel must read as "arrived" the moment the bus is
  // genuinely there, not wait up to 45s for the next Google Directions refresh (the
  // exact gap that made the ETA panel and the coasted map marker disagree -- the marker
  // already halts at a stop on its own clamp, see TuplLiveMap.tsx's capCoastAgeToStop).
  // Same 80m arrival radius nextRouteStop/stopArrivalTimes already use (useTuplApi.ts),
  // checked against the bus's own RAW last-reported position -- not the map's coasted
  // one -- so this can never show "arrived" before the vehicle's real telemetry agrees.
  const hasArrivedAtNextStop = bus?.lat != null && bus?.lon != null
    && hasReachedStop({ lat: bus.lat, lon: bus.lon }, nextStopPos)

  // Shared by loadNextAndEta's 45s tick and the fast per-poll resync effect below, so the
  // two can never compute a different answer for the same inputs. Geotags win over the
  // vehicle's own sticky route_direction, matching inferDirectionFromGeotags/
  // nextRouteStopFromGeotags's own documented precedence.
  const resolveDirAndNext = (
    route: string, lat: number, lon: number, routeDirection: string | null | undefined,
    geotags: TuplGeotagEvent[], allStops: TuplRouteStop[], moving?: boolean,
  ): { dir: 'up' | 'down' | undefined; next: TuplRouteStop | null } => {
    const fromTags = inferDirectionFromGeotags(geotags, { routeCode: route, stops: allStops })
    const sticky = normalizeRunDirection(routeDirection)
    const dir = fromTags ?? sticky ?? undefined
    // moving: false keeps the panel on the stop the bus has actually arrived at/near
    // during a layover instead of advancing to the one after it -- see
    // nextRouteStopFromGeotags's own doc (live bug, confirmed against production TUPL
    // data 2026-10-11: idle buses showed "Arriving" at a stop several stops ahead of
    // where they were actually sitting).
    const next = nextRouteStopFromGeotags(allStops, geotags, { lat, lon }, { routeCode: route, moving })
    return { dir, next }
  }

  // ETA-2: live select only — geotags first (direction), then next stop + Google ETA.
  // Never while duty modal OR breakdown clip replay is open (AC-E3: no Google on
  // playback), and never for a bus with an open breakdown (`selectedIsDown`) -- it is
  // physically stopped, so an ETA/next-stop readout for it is both wrong and a billed
  // Google Directions call for nothing (round-3 adversarial-review finding).
  useEffect(() => {
    if (!selected || duty || clip || selectedIsDown) {
      setSelectedGeotags([])
      setSelectedEta(null)
      setNextStopLabel(null)
      setNextStopPos(null)
      setRunDirection(undefined)
      setAllStopsForSelected([])
      return
    }
    let live = true
    // Drop the previous plate's direction/label immediately so the map does not keep
    // showing the last vehicle's Up/Down leg while geotags for the new plate load.
    setRunDirection(undefined)
    setNextStopLabel(null)
    setNextStopPos(null)
    setSelectedEta(null)
    setAllStopsForSelected([])
    const loadNextAndEta = async (): Promise<void> => {
      let geotags: TuplGeotagEvent[] = []
      try {
        geotags = await fetchTuplGeotags(selected, todayIst())
      } catch {
        geotags = []
      }
      if (!live) return
      setSelectedGeotags(geotags)

      const snap = vehiclesRef.current.find((v) => v.vehicle_id === selected)
      const lat = snap?.lat
      const lon = snap?.lon
      const route = snap?.route_code
      if (lat == null || lon == null || !route) {
        setSelectedEta(null)
        setNextStopLabel(null)
        setNextStopPos(null)
        return
      }

      let allStops
      try {
        // Both legs — needed so source/dest max-seq and geotag inference see the full route.
        allStops = await fetchTuplRouteStops(route)
      } catch {
        if (live) {
          setSelectedEta(null)
          setNextStopLabel(null)
          setNextStopPos(null)
        }
        return
      }
      if (!live) return
      setAllStopsForSelected(allStops)

      const { dir, next } = resolveDirAndNext(
        route, lat, lon, snap?.route_direction, geotags, allStops, snap?.moving,
      )
      setRunDirection(dir)
      setNextStopLabel(next?.name ?? null)
      setNextStopPos(next && Number.isFinite(next.lat) && Number.isFinite(next.lon)
        ? { lat: next.lat, lon: next.lon } : null)

      const dirStops = (dir
        ? allStops.filter((s) => normalizeRunDirection(s.direction) === dir)
        : next
          ? allStops.filter((s) => s.direction === next.direction)
          : allStops
      ).slice().sort((a, b) => a.seq - b.seq)
      const dest = dirStops[dirStops.length - 1]
      if (!dest || !Number.isFinite(dest.lat) || !Number.isFinite(dest.lon)) {
        setSelectedEta(null)
        return
      }
      try {
        const eta = await fetchTuplEta({
          originLat: lat,
          originLon: lon,
          destLat: dest.lat,
          destLon: dest.lon,
          waypointLat: next && next.seq !== dest.seq ? next.lat : undefined,
          waypointLon: next && next.seq !== dest.seq ? next.lon : undefined,
        })
        if (live) setSelectedEta(eta)
      } catch {
        if (live) setSelectedEta(null)
      }
    }
    const stop = pollWhileVisible(() => void loadNextAndEta(), 45_000)
    return () => { live = false; stop() }
    // vehicles intentionally omitted — interval re-reads the latest snap; including the
    // array would re-fire Directions on every live poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, duty, clip, selectedIsDown])

  // Direction + next-stop, resynced on every live poll -- NOT folded into loadNextAndEta
  // above, whose 45s cadence exists only because it also pays for a Google Directions
  // call (AC-E3's cost concern). Between those 45s ticks the map bead kept showing the
  // PREVIOUS direction's stop sequence after a real direction change (reaching a
  // terminus and turning around, or a gps_fallback route_source correcting itself once
  // scheduling data resolves) -- live, a bus running Down highlighted a Down stop it had
  // already passed because runDirection was still Up from up to 45s earlier, reading as
  // "next stop" pointing behind the bus. This effect costs nothing extra over the network:
  // `selectedGeotags` and `allStopsForSelected` are both already in memory from
  // loadNextAndEta, and `vehicles` is the same live poll the map itself renders from, so
  // this just recomputes the same precedence (geotags win, sticky telemetry direction is
  // the fallback) at the cadence the bead actually needs.
  useEffect(() => {
    if (!selected || duty || clip || selectedIsDown) return
    if (allStopsForSelected.length === 0) return
    const snap = vehicles.find((v) => v.vehicle_id === selected)
    if (!snap || snap.lat == null || snap.lon == null || !snap.route_code) return
    const { dir, next } = resolveDirAndNext(
      snap.route_code, snap.lat, snap.lon, snap.route_direction, selectedGeotags, allStopsForSelected,
      snap.moving,
    )
    setRunDirection(dir)
    setNextStopLabel(next?.name ?? null)
    // selectedIsDown intentionally omitted from deps (round-5 adversarial-review finding:
    // confirmed by mutation that it was inert there) -- the OWN condition above still
    // checks it (correctness within a render), but listing it as a dependency added
    // nothing: `vehicles` already forces a re-run on the exact same tick selectedIsDown
    // would change on (both come from the same 8s refresh() poll), and once
    // `allStopsForSelected` is cleared to `[]` by the other effect the very next render,
    // this effect's own `allStopsForSelected.length === 0` guard already short-circuits
    // it regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, selected, duty, clip, selectedGeotags, allStopsForSelected])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Bump both "last call wins" refs, not just clear the state they guard --
        // otherwise a playDuty()/openEvidence() fetch already in flight when Escape fires
        // still lands afterward and pops its modal open for a plate the operator already
        // dismissed (adversarial-review finding: the ref was only ever bumped by a NEW
        // call, never by closing/escaping an old one). Also force both loading flags off
        // -- bumping the ref makes the fetch's own `finally` guard skip clearing them
        // (round-2 adversarial-review finding: that guard only fires for the SAME call
        // that set the flag, so a bump from anywhere else left the button permanently
        // disabled/stuck on "Loading…" until a reload).
        ++dutyReqRef.current
        ++evidenceReqRef.current
        setDutyLoading(false)
        setEvidenceLoading(false)
        if (duty || clip) {
          // A modal is open -- Escape closes JUST it, not the right-hand panel
          // underneath. This listener and the modal's own Escape listener (TuplDutyPlayback.
          // tsx/TuplEvidence.tsx) both fire on the same keypress; this one used to
          // unconditionally clear `selected` too, so closing the evidence/duty modal via
          // Escape also deselected the vehicle and collapsed the right-hand panel
          // (round-3 adversarial-review finding). A second Escape, with no modal open,
          // still deselects as before.
          setDuty(null); setClip(null); setClipAlerts([]); setClipAlertsTruncated(false)
          return
        }
        setSelected(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [duty, clip])

  const liveCount = onAir
  const alertItems = actionables.filter((i) => i.kind === 'alert')
  const oldestBd = breakdowns.reduce<number | null>(
    (acc, b) => (b.opened_ts != null && (acc == null || b.opened_ts < acc) ? b.opened_ts : acc),
    null,
  )
  // SSE overlay on top of the 2.5s poll -- additive, not a replacement (see
  // useTuplStatusEvents' own comment). on_air/seen_s/route_code/driver_name/
  // schedule_code stay whatever the poll says: on_air specifically cannot be pushed (a
  // vehicle going silent produces no frame to push), and the rest come from a slower,
  // TIMS-duty-joined cache SSE does not carry. Only position/speed/heading/soc/moving
  // -- the fields that change every telemetry tick -- update ahead of the next poll.
  const liveVehicles = vehicles.map((v) => {
    const ev = liveStatus[v.vehicle_id]
    if (!ev || now - ev.updated_ts > TUPL_LIVE_EVENT_STALE_MS) return v
    // ev.lat/lon/speed_kph/heading_deg/soc_pct are ALL typed `number | null`
    // (TuplStatusEvent, useTuplStatusEvents.ts) -- a telemetry frame with no GPS fix
    // (confirmed shaped live: ignition-on-but-no-fix frames are routine on this feed)
    // pushes a `tupl_status` event with these null, which an unconditional overwrite
    // would blindly apply, regressing a perfectly good poll-sourced position to nothing
    // -- the map's own marker loop (TuplLiveMap.tsx) filters on
    // Number.isFinite(lat/lon), so this silently dropped the vehicle from the map
    // entirely until the next poll happened to have a real fix. `moving` already had
    // this exact guard (`ev.moving ?? v.moving`); the rest did not.
    return {
      ...v,
      lat: ev.lat ?? v.lat, lon: ev.lon ?? v.lon,
      speed_kph: ev.speed_kph ?? v.speed_kph,
      heading_deg: ev.heading_deg ?? v.heading_deg,
      soc_pct: ev.soc_pct ?? v.soc_pct,
      moving: ev.moving ?? v.moving,
    }
  })
  const roster = [...liveVehicles].sort((a, b) => {
    const rank = (v: TuplVehicle): number => (
      down.has(v.vehicle_id) ? 0 : v.on_air ? (v.moving ? 1 : 2) : 3
    )
    return rank(a) - rank(b) || a.vehicle_id.localeCompare(b.vehicle_id)
  })
  // Alert/breakdown pin for the selected vehicle (root cause 2) -- TuplLiveMap's own pan
  // effect (resolveBusFocus, busFocus.ts, ~TuplLiveMap.tsx:1555-1569) falls back to this
  // when the vehicle has no live GPS fix, so an alert-tile click for an offline/stale
  // plate still pans the map instead of silently doing nothing. An OPEN breakdown's pin
  // wins over an alert pin for the same vehicle -- same precedence fleetException()/
  // breakdownByVehicle actually use (FleetDrawer, below), both built from the open
  // `breakdowns` array only, NOT `openBd` (which also matches a cleared/resolved row) --
  // a plate with a breakdown resolved hours ago plus a brand-new alert must pan to the
  // alert's own pin, not the old incident's (adversarial-review finding).
  const openBreakdownPin = breakdowns.find((b) => b.vehicle_id === selected)
  // The SAME alert the fleet card's exception chip names for this plate (representativeAlertFor,
  // shared with alertByVehicle below) -- round-3 adversarial-review finding: this used to
  // run its own precedence (most recent alert with a pin, ignoring severity), which could
  // pick a DIFFERENT alert than the chip's own (most recent, but CRITICAL always wins).
  // If the representative alert itself has no usable coordinates, the fallback is simply
  // absent rather than silently substituting a different alert's pin that doesn't match
  // what the chip is telling the operator.
  const selectedRepresentativeAlert = selected ? representativeAlertFor(selected, alertItems) : undefined
  const selectedAlertPin = selectedRepresentativeAlert
    && selectedRepresentativeAlert.lat != null && selectedRepresentativeAlert.lon != null
    ? selectedRepresentativeAlert
    : undefined
  const focusFallback = openBreakdownPin && openBreakdownPin.lat != null && openBreakdownPin.lon != null
    ? { lat: openBreakdownPin.lat, lon: openBreakdownPin.lon }
    : selectedAlertPin
      ? { lat: selectedAlertPin.lat, lon: selectedAlertPin.lon }
      : null
  // Sustained-condition alerts (EXCESSIVE_IDLING/UNSCHEDULED_STOP) are not ts-aged --
  // see isSustainedAlertStillOpen's own comment for the full rule. `v.on_air` is the
  // staleness gate (adversarial review HIGH-1): a never-reported/gone-silent bus
  // synthesizes `moving: false` too (serving/tupl.py's gps_fallback rows), so
  // `moving === false` ALONE cannot tell "still genuinely idling" apart from "we have
  // no idea where this bus is" -- `on_air` is the same signal this file's own roster
  // rank (above) already trusts for exactly that distinction. No charging/depot/
  // terminus signal is exposed on TuplVehicle's live payload at all (unlike Ultratech/
  // Wonder Cement's charger_state or STAR's ignition_status/charging_status) --
  // isExcludedState is left at its default `false` here, a documented gap (HIGH-2 is
  // only partially closed for TUPL), not an assumed "definitely still idling".
  const liveByVehicle = new Map(liveVehicles.map((v) => [v.vehicle_id, v]))

  /** Whole-duty playback: fetch today's GPS trail + geotags for this plate, filter
   * today's actionables to it client-side (no dedicated endpoint for that slice), force
   * a fresh TIMS read for this plate's driver/schedule (not the ambient ~60s live-poll
   * cache -- GET /api/tupl/duties already forces its own re-poll server-side, see
   * serving/tupl.py's duties()/refresh_duties(force=True)), and hand the result to the
   * duty-playback modal. Closes any open evidence clip -- the two are independent modals,
   * but still only one replay should be on screen at a time. */
  async function playDuty(vehicleId: string): Promise<void> {
    const reqId = ++dutyReqRef.current
    // Also invalidate any in-flight openEvidence() fetch -- without this, starting duty
    // playback while an evidence fetch is still pending let BOTH modals pop open once the
    // slower one landed (adversarial-review finding: each guard only protected against a
    // NEWER call of its OWN kind, never the other kind). Force its loading flag off too --
    // bumping the ref alone makes that fetch's own `finally` skip clearing it, which
    // otherwise left "View evidence" stuck on "Loading evidence…", disabled, forever
    // (round-2 adversarial-review finding).
    ++evidenceReqRef.current
    setEvidenceLoading(false)
    setDutyLoading(true)
    setClip(null)
    setClipAlerts([])
    setClipAlertsTruncated(false)
    try {
      const nowIst = new Date()
      const mins = nowIst.getUTCHours() * 60 + nowIst.getUTCMinutes() + 330 // UTC -> IST
      const [trackRes, stops, duties, dayAlerts] = await Promise.all([
        fetchTuplTrack(vehicleId, Math.min(1440, Math.max(60, mins))),
        fetchTuplGeotags(vehicleId),
        // A TIMS hiccup here must not fail the GPS/stops half of this load -- the duty
        // still opens, just with an empty driver/schedule, same as a plate with no TIMS
        // duty today.
        fetchTuplDuties().catch(() => []),
        // Feature 12: full IST-day alerts for this plate (not the capped actionables queue).
        fetchTuplVehicleDayAlerts(vehicleId, todayIst()).catch(() =>
          actionables.filter((a) => a.kind === 'alert' && a.vehicle_id === vehicleId),
        ),
      ])
      // A newer playDuty() call started (and possibly already finished) while this one was
      // in flight -- its result belongs to a plate the operator has already moved on from.
      if (dutyReqRef.current !== reqId) return
      const points = trackRes.track
      const started = points[0]?.ts ?? Date.now() - 3_600_000
      const ended = points[points.length - 1]?.ts ?? Date.now()
      const tims = tuplDutyFor(vehicleId, duties)
      // From the live snapshot, not the TIMS duty row or the trip-alerts' own route_code
      // (a different data source) -- null, never '', when the plate isn't currently on air.
      const liveVehicle = vehicles.find((v) => v.vehicle_id === vehicleId)
      setDuty({
        vehicle_id: vehicleId,
        started_ts: started,
        ended_ts: ended,
        points,
        stops,
        alerts: dayAlerts,
        route_code: liveVehicle ? liveVehicle.route_code : null,
        driver_name: (tims?.drivername as string) || null,
        schedule_code: (tims?.schedulecode as string) || null,
        schedule_shift: (tims?.scheduleshift as string) || null,
      })
    } catch (e) {
      if (dutyReqRef.current !== reqId) return
      setErr(e instanceof Error ? e.message : 'duty playback load failed')
    } finally {
      if (dutyReqRef.current === reqId) setDutyLoading(false)
    }
  }

  /** Explicit "View evidence" action (root cause 1/3 fix) -- the breakdown evidence clip
   * no longer auto-opens on selection; an operator must ask for it. Fetches the
   * breakdown's own replay clip, then that plate's day alerts for the clip's OWN IST
   * day(s) -- not "today" (a lead-in clip for a breakdown reported just after IST
   * midnight starts before midnight, on the PREVIOUS calendar day; fetching only "today"
   * silently dropped those alerts, adversarial-review finding) -- filtered to the clip's
   * window (the breakdown-replay endpoint itself carries no alert data -- serving/
   * tupl.py's breakdown_replay() has no join to any alert table), and opens TuplEvidence
   * as its own modal with its own embedded map -- never touching the shared live map.
   * Closes any open duty modal first and invalidates any in-flight playDuty() fetch --
   * same "only one replay on screen" invariant playDuty() enforces the other way. */
  async function openEvidence(vehicleId: string): Promise<void> {
    const bd = breakdowns.find((b) => b.vehicle_id === vehicleId)
      ?? resolved.find((b) => b.vehicle_id === vehicleId)
    if (!bd) return
    const reqId = ++evidenceReqRef.current
    // Also invalidate any in-flight playDuty() fetch, and force its loading flag off --
    // same reciprocal guard + stuck-button fix playDuty() applies the other way (round-2
    // adversarial-review finding).
    ++dutyReqRef.current
    setDutyLoading(false)
    setDuty(null)
    setEvidenceLoading(true)
    try {
      const c = await fetchTuplReplay(bd.breakdown_id)
      if (evidenceReqRef.current !== reqId) return
      const windowEnd = c.ended_ts ?? Date.now()
      const { dates, truncated } = istDatesBetween(c.started_ts, windowEnd)
      const dayAlerts = (await Promise.all(
        dates.map((d) => fetchTuplVehicleDayAlerts(vehicleId, d).catch(() => [])),
      )).flat()
      // A newer openEvidence() call started while this one was in flight, or the operator
      // dismissed it (Escape/close) before it resolved -- either way this result belongs
      // to a plate/breakdown the operator has already moved on from.
      if (evidenceReqRef.current !== reqId) return
      setClip(c)
      // Alerts are only ever fetched for the dates istDatesBetween returned -- filtering
      // against the clip's own full started_ts..windowEnd is still correct (nothing
      // outside the fetched dates could match).
      setClipAlerts(dayAlerts.filter(
        (a) => a.opened_ts != null && a.opened_ts >= c.started_ts && a.opened_ts <= windowEnd,
      ))
      // `truncated` is istDatesBetween's own computation (see its doc comment for why
      // that must live there, not be reconstructed from `dates[0]` after the fact).
      setClipAlertsTruncated(truncated)
    } catch (e) {
      if (evidenceReqRef.current !== reqId) return
      setErr(e instanceof Error ? e.message : 'evidence clip load failed')
    } finally {
      if (evidenceReqRef.current === reqId) setEvidenceLoading(false)
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0 px-3 pt-3">
        <TopBar
          clock={`${new Date(now).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
          })} · ${new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}`}
          live={err == null && loaded}
          rate={`${liveCount} buses`}
          user={userLabel}
          fleets={fleetPills(true)}
          activeFleet="tupl"
          onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
          nav={surfacePills('tupl')}
          activeNav="live"
          onNav={(id) => navigate(pathFor('tupl', id === 'reports' ? 'reports' : id === 'charging' ? 'charging' : 'live'))}
          onSettings={() => navigate('/settings')}
        />
      </div>

      <div className="shrink-0 space-y-3 px-3 pt-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className={EYEBROW}>Ulhasnagar · UMT depot</p>
            <h1 className="font-display text-[22px] font-semibold tracking-tight text-[#0F172A]">
              Command Centre
            </h1>
          </div>
          <FilterChips
            active={view}
            onPick={(id) => navigate(id === 'live' ? '/bus-ops' : `/bus-ops/${id}`)}
            options={[
              { id: 'live', label: 'Live ops' },
              { id: 'trip-alerts', label: 'Flagged Trips', count: tripAlertsTotal },
              { id: 'board', label: 'Arrivals & departures', count: board.length },
            ]}
          />
        </div>
        <KpiStrip>
          <KpiCard label="Live vehicles" value={liveCount} unit={`of ${register || '—'}`} dot
                   tone={liveCount ? 'ok' : 'danger'}
                   sub={register ? `${Math.max(0, register - liveCount)} silent` : 'waiting for snapshots'} />
          <KpiCard label="Moving now" value={moving} unit="buses" dot tone="neutral"
                   sub={liveCount ? `${Math.max(0, liveCount - moving)} idle` : 'no live data'} />
          {/* down.size, not breakdowns.length -- breakdowns is raw open-breakdown ROWS,
              and a vehicle can carry more than one (a second incident logged before the
              first was resolved, or a reopened-then-relogged case). Confirmed live: this
              card read 62 while the roster's own DOWN filter -- built from this same
              `down` Set, see fleetStatus() -- showed 48 buses, because the card counted
              rows where the roster counts distinct vehicles. down.size is what "buses
              down" actually means. */}
          <KpiCard label="Open breakdowns" value={down.size} unit="buses down" dot
                   tone={down.size ? 'danger' : 'ok'}
                   sub={oldestBd != null ? `longest open ${ago(oldestBd)}` : 'none open — fleet clear'} />
          <KpiCard label="Alerts" value={todayAlertCount} unit="today" dot
                   tone={todayAlertCount ? 'warn' : 'neutral'} />
          <KpiCard label="Km today" value={daySummary.live_km} dot
                   tone={daySummary.dead_trips || daySummary.lost_trips ? 'warn' : 'neutral'}
                   sub={`${daySummary.dead_trips} dead · ${daySummary.lost_trips} lost trips`} />
        </KpiStrip>
      </div>

      <div className="flex min-h-0 flex-1 gap-3 p-3">
        <FleetDrawer
          open={fleetOpen}
          onToggle={() => setFleetOpen((o) => !o)}
          roster={roster}
          loaded={loaded}
          liveCount={liveCount}
          register={register}
          down={down}
          breakdowns={breakdowns}
          // Live-tile TTL (.claude/work/SPEC.md): a fleet-card exception chip must stop
          // showing an alert 5 minutes after the alert's OWN opened_ts, not from whenever
          // the client happened to first render it. Filtered here, right before the prop
          // boundary, so alertItems itself stays the raw (unfiltered) list for any other
          // consumer -- `now` already ticks every 1s above, so this re-evaluates on the
          // same cadence as every other live-elapsed display in this file, no new timer.
          // Sustained-condition kinds (EXCESSIVE_IDLING/UNSCHEDULED_STOP) are exempted
          // from the ts TTL entirely -- isSustainedAlertStillOpen (current telemetry,
          // same poll, plus the staleness gate/resurrection latch/day backstop its own
          // comment documents) decides their visibility instead, per user decision
          // 2026-10-28.
          alertItems={alertItems.filter((a) => {
            if (!isSustainedConditionKind(a.category ?? '')) return isAlertVisible(a.opened_ts ?? 0, now)
            const v = liveByVehicle.get(a.vehicle_id)
            return isSustainedAlertStillOpen({
              alertId: String(a.id),
              ts: a.opened_ts ?? 0,
              now,
              isLive: v?.on_air === true,
              isMoving: v?.moving === true,
            }, sustainedAlertDeadSet)
          })}
          selected={selected}
          onSelectVehicle={(id) => pointAtVehicle(id)}
          onPlayDuty={(id) => { navigate('/bus-ops'); pointAtVehicle(id); void playDuty(id) }}
          onMarkBreakdown={(id) => setBreakdownFormFor(id)}
        />
      {view === 'board' ? (
        <div className="min-h-0 flex-1">
          <TuplBoard
            rows={board}
            now={now}
            err={boardErr}
            loaded={boardLoaded}
            onSelectVehicle={(id) => pointAtVehicle(id)}
            onPlayDuty={(id) => { navigate('/bus-ops'); pointAtVehicle(id); void playDuty(id) }}
            onViewTimeline={(id) => setTimelineVehicleId(id)}
          />
        </div>
      ) : view === 'trip-alerts' ? (
        <TuplTripAlerts />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_360px] gap-3">
          <section className={clsx(PANEL, 'relative min-h-0 overflow-hidden rounded-[18px]')}>
          <TuplLiveMap
            vehicles={liveVehicles}
            fleetVehicles={vehicles}
            routes={routes}
            stages={stages}
            down={down}
            selected={selected}
            onSelect={(id) => pointAtVehicle(id)}
            focusTick={focusTick}
            focusFallback={focusFallback}
            stopDirection={runDirection}
            onStopDirectionChange={setRunDirection}
          />
            <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2">
              <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,0.08)] backdrop-blur">
                OpenStreetMap · {routes.length} routes
              </span>
              <span className="rounded-full bg-white/80 px-3 py-1 text-[11px] text-[#64748B] backdrop-blur">
                {moving} moving · {liveCount} live
              </span>
            </div>
            {err && (
              // z-20: above TuplLiveMap's own fleet-status legend (z-10, bottom-left) --
              // a live-feed failure must always stay readable even if the two still ever
              // overlap. pointer-events-none: this banner is text-only, full-width
              // (left-3 right-3), and spans under TuplLiveMap's own bottom-right 2D/3D +
              // Photoreal buttons (z-10) -- without it, z-20 made those unclickable
              // whenever the live feed was down, a real regression this fix closes.
              <div className="pointer-events-none absolute bottom-3 left-3 right-3 z-20 rounded-[12px] border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.10)] px-3 py-2 text-[12px] font-medium text-[#9F1239]">
                {err}
              </div>
            )}
          </section>

          <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto">
            <section className={clsx(PANEL, 'shrink-0 p-4')}>
              {selected ? (
                <>
                  <p className={EYEBROW}>Selected bus</p>
                  <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <h2 className="font-mono text-[18px] font-bold text-[#0F172A]">{selected}</h2>
                    {(efficiency?.live_leg?.kwh_per_km ?? efficiency?.last_leg?.kwh_per_km) != null && (
                      <span className="font-mono text-[12px] font-semibold tabular-nums text-[#0369A1]">
                        {efficiency?.live_leg?.kwh_per_km ?? efficiency?.last_leg?.kwh_per_km} kWh/km
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[12px] text-[#64748B]">
                    {bus?.vehicletype || 'class unknown'}
                    {bus?.route_code ? ` · lake route ${bus.route_code}` : ''}
                    {bus?.speed_kph != null ? ` · ${Math.round(bus.speed_kph)} kph` : ' · — kph'}
                    {` · ${bus?.on_air ? 'live' : 'stale'} ${bus ? `${Math.round(bus.seen_s)}s` : '—'}`}
                  </p>
                  {bus?.driver_name ? (
                    <p className="mt-1 text-[13px] font-medium text-[#0F172A]">
                      {bus.driver_name}
                      {bus.schedule_shift ? ` · ${bus.schedule_shift}` : ''}
                      {bus.schedule_code ? ` · ${bus.schedule_code}` : ''}
                    </p>
                  ) : (
                    <p className="mt-1 text-[12px] text-[#94A3B8]">No TIMS duty on this plate today</p>
                  )}
                  <div className="mt-2 flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={dutyLoading}
                      onClick={() => void playDuty(selected)}
                      className="flex items-center gap-1.5 rounded-full bg-[#0F172A] px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50"
                    >
                      <Play size={11} />
                      {dutyLoading ? 'Loading duty…' : 'Play duty'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBreakdownFormFor(selected)}
                      className="flex items-center gap-1.5 rounded-full border border-[rgba(244,63,94,0.30)] bg-[rgba(244,63,94,0.08)] px-3 py-1.5 text-[11px] font-semibold text-[#9F1239]"
                    >
                      <AlertOctagon size={11} />
                      {down.has(selected) ? 'Update breakdown' : 'Mark breakdown'}
                    </button>
                    {openBd && (
                      // Explicit action (root cause 1/3 fix): opening the evidence clip is
                      // never a side effect of selection -- it only ever fetches/opens when
                      // the operator clicks this. Disabled + relabeled while loading, same
                      // pattern as Play duty's dutyLoading -- also closes the window for a
                      // double-click to fire a second redundant fetch.
                      <button
                        type="button"
                        disabled={evidenceLoading}
                        onClick={() => void openEvidence(selected)}
                        className="flex items-center gap-1.5 rounded-full border border-[rgba(37,99,235,0.30)] bg-[rgba(37,99,235,0.08)] px-3 py-1.5 text-[11px] font-semibold text-[#1D4ED8] disabled:opacity-50"
                      >
                        <Eye size={11} />
                        {evidenceLoading ? 'Loading evidence…' : 'View evidence'}
                      </button>
                    )}
                  </div>
                  <SocBar value={bus?.soc_pct ?? null} />
                  {(efficiency?.live_leg?.kwh_per_km != null
                    || efficiency?.live_leg?.soc_per_km != null
                    || efficiency?.last_leg?.kwh_per_km != null
                    || efficiency?.last_leg?.soc_per_km != null) && (
                    <div className="mt-2 grid grid-cols-2 gap-2 rounded-[10px] bg-[#F8FAFC] px-2.5 py-2">
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">kWh/km</p>
                        <p className="font-mono text-[13px] font-semibold tabular-nums text-[#0F172A]">
                          {efficiency?.live_leg?.kwh_per_km ?? efficiency?.last_leg?.kwh_per_km ?? '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">km/SoC</p>
                        <p className="font-mono text-[13px] font-semibold tabular-nums text-[#0F172A]">
                          {(() => {
                            // km per 1 percentage-point of SoC consumed -- the inverse of
                            // the backend's own soc_per_km (serving/tupl.py: (soc_start -
                            // soc_end) / distance_km), easier to read as "range" than
                            // "SoC spent per km." Computed client-side since the API has
                            // no field for it; not shown for 0 (division by zero reads as
                            // infinite range, not a real number).
                            const socPerKm = efficiency?.live_leg?.soc_per_km ?? efficiency?.last_leg?.soc_per_km
                            return socPerKm ? (1 / socPerKm).toFixed(1) : '—'
                          })()}
                        </p>
                      </div>
                    </div>
                  )}
                  {(nextStopLabel || selectedEta) && (
                    <div className="mt-2 rounded-[10px] bg-[#F8FAFC] px-2.5 py-2 text-[12px] text-[#0F172A]">
                      {nextStopLabel ? (
                        <p>
                          <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">Next stop </span>
                          <span className="font-semibold animate-pulse">{nextStopLabel}</span>
                        </p>
                      ) : null}
                      {selectedEta?.next_stop_duration_s != null || selectedEta?.destination_duration_s != null ? (
                        <p className="mt-1 font-mono tabular-nums text-[11px] text-[#0369A1]">
                          {hasArrivedAtNextStop
                            // The bus's own last-reported position is within the same 80m
                            // arrival radius nextRouteStop() uses to call a stop "reached" --
                            // show that now, from the live poll, rather than the Directions
                            // estimate fetched up to 45s ago (the map marker's own coast-
                            // clamp already halts at this same stop; this keeps the two
                            // readings agreeing instead of one saying "arriving" while the
                            // other still counts down).
                            ? 'ETA next: Arriving'
                            : selectedEta.next_stop_duration_s != null
                              ? `ETA next ~${Math.round(selectedEta.next_stop_duration_s / 60)}m`
                              : 'ETA next —'}
                          {' · '}
                          {selectedEta.destination_duration_s != null
                            ? `dest ~${Math.round(selectedEta.destination_duration_s / 60)}m`
                            : 'dest —'}
                        </p>
                      ) : selectedEta?.duration_in_traffic_s != null || selectedEta?.duration_s != null ? (
                        <p className="mt-1 font-mono tabular-nums text-[11px] text-[#0369A1]">
                          ETA dest ~{Math.round((selectedEta.duration_in_traffic_s ?? selectedEta.duration_s ?? 0) / 60)}m traffic
                        </p>
                      ) : selectedEta?.error ? (
                        <p className="mt-1 text-[11px] text-[#94A3B8]">ETA unavailable</p>
                      ) : null}
                    </div>
                  )}
                  {selectedGeotags.length > 0 && (
                    <div className="mt-2">
                      <p className={clsx(EYEBROW, 'mb-1')}>Today&apos;s geotags</p>
                      <ul className="max-h-28 space-y-1 overflow-y-auto pr-1">
                        {selectedGeotags.slice(-12).reverse().map((g, i) => (
                          <li key={`${g.ts}-${i}`} className="flex gap-2 text-[11px] text-[#475569]">
                            <span className="shrink-0 font-mono tabular-nums text-[#94A3B8]">
                              {new Date(g.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })}
                            </span>
                            <span className="min-w-0 truncate">{g.name || g.kind}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {down.has(selected) ? (
                    <p className="mt-2 text-[12px] font-semibold text-[#9F1239]">
                      Breakdown open
                    </p>
                  ) : openBd?.cleared_ts ? (
                    <p className="mt-2 text-[12px] font-medium text-[#047857]">Resolved.</p>
                  ) : (
                    <p className="mt-2 text-[12px] text-[#64748B]">Mark down to open a call-centre incident.</p>
                  )}
                </>
              ) : (
                <EmptyState
                  icon={<Radio size={18} />}
                  title="Select a bus"
                  body="Click the map or a queue row to see live details."
                />
              )}
            </section>
          </aside>
        </div>
      )}
      </div>
      {breakdownFormFor && (
        <TuplBreakdownForm
          vehicleId={breakdownFormFor}
          existing={
            breakdowns.find((b) => b.vehicle_id === breakdownFormFor)
            ?? resolved.find((b) => b.vehicle_id === breakdownFormFor)
            ?? null
          }
          codes={codes}
          onClose={() => setBreakdownFormFor(null)}
          onSaved={() => void refresh()}
        />
      )}
      {timelineVehicleId && (
        <TuplVehicleTimelineModal
          vehicleId={timelineVehicleId}
          rows={timelineRows}
          loading={timelineLoading}
          onClose={() => setTimelineVehicleId(null)}
        />
      )}
      {duty && (
        // Keyed on vehicle_id: playDuty() can replace `duty` with a different plate's clip
        // without an intervening null (the "last call wins" edge case), and the modal's
        // own at/playing/speed/range local state must not survive across that -- a plain
        // prop update would leave the previous plate's scrub position and play state
        // bleeding into the new plate's playback. Remounting is simpler and more robust
        // than threading a manual reset effect through every piece of that state.
        <TuplDutyPlayback
          key={duty.vehicle_id}
          duty={duty}
          onClose={() => { ++dutyReqRef.current; setDutyLoading(false); setDuty(null) }}
        />
      )}
      {clip && (
        // Explicit "View evidence" action only (root cause 1/3 fix) -- own modal, own
        // embedded map, never auto-opened by selection and never sharing the live map's
        // vehicle marker. Keyed on breakdown_id, same reasoning as the duty modal's own
        // vehicle_id key above: a second openEvidence() call can replace `clip` with a
        // different breakdown's without an intervening null, and this modal's own
        // at/playing/speed local state must not survive across that.
        <TuplEvidence
          key={clip.breakdown_id}
          clip={clip}
          alerts={clipAlerts}
          alertsTruncated={clipAlertsTruncated}
          onClose={() => {
            ++evidenceReqRef.current
            setEvidenceLoading(false)
            setClip(null)
            setClipAlerts([])
            setClipAlertsTruncated(false)
          }}
        />
      )}
    </div>
  )
}

const FLEET_FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'moving', label: 'MOVING' },
  { id: 'idle', label: 'IDLE' },
  { id: 'down', label: 'DOWN' },
  { id: 'offline', label: 'OFFLINE' },
]

const FLEET_SORTS = [
  { id: 'status', label: 'Status' },
  { id: 'speed', label: 'Speed' },
  { id: 'soc', label: 'SoC' },
]

function fleetStatus(v: TuplVehicle, isDown: boolean): VehicleStatus {
  if (isDown) return 'alert'
  if (!v.on_air) return 'offline'
  return v.moving ? 'moving' : 'idle'
}

const FLEET_STATUS_LABEL: Record<VehicleStatus, string> = {
  moving: 'Moving', idle: 'Idle', loading: 'Loading', alert: 'Down', offline: 'Offline',
}

/** Is `a` a better "representative" alert for its vehicle than whatever `current` already
 * is? Most recent wins (assuming ts-DESC input order, `current` is already the more
 * recent of the two), UNLESS `a` is CRITICAL and `current` isn't, which always wins
 * regardless of recency. Shared by alertByVehicle (FleetDrawer below, one pass building a
 * map for the whole roster) and representativeAlertFor (TuplOpsShell above, one plate) so
 * the fleet card's exception chip and the live map's focus-fallback pin can never
 * disagree about which alert represents the same vehicle (round-3 adversarial-review
 * finding: the two used to run their own, slightly different precedence). */
function outranksAsRepresentative(a: TuplActionable, current: TuplActionable | undefined): boolean {
  return !current || (a.severity === 'CRITICAL' && current.severity !== 'CRITICAL')
}

/** The one alert that "represents" a single vehicle -- see outranksAsRepresentative.
 * `items` is already ts DESC from the API. */
function representativeAlertFor(
  vehicleId: string, items: TuplActionable[],
): TuplActionable | undefined {
  let best: TuplActionable | undefined
  for (const a of items) {
    if (a.vehicle_id !== vehicleId) continue
    if (outranksAsRepresentative(a, best)) best = a
  }
  return best
}

/** Fleet card exception chip: breakdown always wins over a driving/camera alert (a bus
 * already marked down does not need its idling alert competing for the same slot), which
 * in turn wins over the plain low-SoC fallback -- same priority Ultratech's own
 * CommandCentre.tsx toStatus/exception block uses. */
function fleetException(
  v: TuplVehicle, breakdown: TuplBreakdown | undefined, alert: TuplActionable | undefined,
): { icon: 'clock' | 'zap' | 'power' | 'alert'; text: string; tone: 'warn' | 'danger' | 'info' } | null {
  if (breakdown) {
    return {
      icon: 'power',
      text: breakdown.category || 'down',
      tone: breakdown.severity === 'immobilised' ? 'danger' : 'warn',
    }
  }
  if (alert) {
    const stopKind = alert.category === 'EXCESSIVE_IDLING' || alert.category === 'UNSCHEDULED_STOP'
    return {
      icon: stopKind ? 'clock' : 'alert',
      text: alert.note || alert.category || 'alert',
      tone: alert.severity === 'CRITICAL' ? 'danger' : 'warn',
    }
  }
  if (v.soc_pct != null && v.soc_pct < 20) {
    return { icon: 'zap', tone: 'danger', text: `${Math.round(v.soc_pct)}% SoC` }
  }
  return null
}

function FleetDrawer({
  open, onToggle, roster, loaded, liveCount, register, down, breakdowns, alertItems,
  selected, onSelectVehicle, onPlayDuty, onMarkBreakdown,
}: {
  open: boolean
  onToggle: () => void
  roster: TuplVehicle[]
  loaded: boolean
  liveCount: number
  register: number
  down: Set<string>
  breakdowns: TuplBreakdown[]
  alertItems: TuplActionable[]
  selected: string | null
  onSelectVehicle: (id: string) => void
  onPlayDuty: (id: string) => void
  onMarkBreakdown: (id: string) => void
}): JSX.Element {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [sort, setSort] = useState('status')

  // One breakdown/alert per plate for the fleet card's exception chip -- same priority
  // Ultratech's own CommandCentre.tsx uses (toStatus/exception block: a down truck's own
  // note always wins over its telemetry alert). actionables is already ts DESC from the
  // API, so the first alert seen per plate is the most recent one.
  const breakdownByVehicle = useMemo(() => {
    const m = new Map<string, TuplBreakdown>()
    for (const b of breakdowns) if (!m.has(b.vehicle_id)) m.set(b.vehicle_id, b)
    return m
  }, [breakdowns])
  const alertByVehicle = useMemo(() => {
    const m = new Map<string, TuplActionable>()
    for (const a of alertItems) {
      if (outranksAsRepresentative(a, m.get(a.vehicle_id))) m.set(a.vehicle_id, a)
    }
    return m
  }, [alertItems])

  const q = query.trim().toLowerCase()
  let rows = roster.filter((v) => (
    !q || v.vehicle_id.toLowerCase().includes(q) || (v.driver_name || '').toLowerCase().includes(q)
  )).filter((v) => {
    if (filter === 'all') return true
    return fleetStatus(v, down.has(v.vehicle_id)) === (filter === 'down' ? 'alert' : filter)
  })
  if (sort === 'speed') {
    rows = [...rows].sort((a, b) => (b.speed_kph ?? -1) - (a.speed_kph ?? -1))
  } else if (sort === 'soc') {
    rows = [...rows].sort((a, b) => (a.soc_pct ?? 101) - (b.soc_pct ?? 101))
  }
  // 'status' keeps the roster's own down/moving/idle/offline priority order as-is.

  return (
    <aside className={clsx(
      'flex min-h-0 shrink-0 flex-col transition-[width] duration-200',
      open ? 'w-[320px]' : 'w-[52px]',
    )}>
      <Panel className="min-h-0 flex-1" pad={false}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex shrink-0 items-center gap-2 border-b border-[rgba(15,23,42,0.06)] px-3 py-2.5">
            <button
              type="button"
              onClick={onToggle}
              aria-label={open ? 'Collapse fleet drawer' : 'Expand fleet drawer'}
              title={open ? 'Collapse' : 'Fleet'}
              className="flex shrink-0 items-center gap-1.5 rounded-[8px] p-1 text-[#0369A1] transition-colors hover:bg-[#EEF2F7]"
            >
              <Bus size={14} />
              {open ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
            </button>
            {open && (
              <>
                <span className="text-[12px] font-semibold text-[#0F172A]">Fleet</span>
                <span className="ml-auto font-mono text-[11px] font-bold tabular-nums text-[#047857]">
                  {liveCount}
                  <span className="font-medium text-[#94A3B8]">/{register || '—'}</span>
                </span>
              </>
            )}
          </div>
          {open ? (
            <div className="flex min-h-0 flex-1 flex-col gap-2.5 p-3">
              <SearchBox value={query} onChange={setQuery} placeholder="Plate or driver…" />
              <FilterChips
                options={FLEET_FILTERS.map((f) => ({
                  ...f,
                  count: f.id === 'all' ? roster.length
                    : roster.filter((v) => fleetStatus(v, down.has(v.vehicle_id)) === (f.id === 'down' ? 'alert' : f.id)).length,
                }))}
                active={filter}
                onPick={setFilter}
              />
              <SortBar options={FLEET_SORTS} active={sort} onPick={setSort} />
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {!loaded && <QueueSkeleton />}
                {loaded && rows.length === 0 && (
                  <EmptyState icon={<Bus size={22} />} title="No vehicles match"
                              body="Widen the filter, or clear the search." />
                )}
                {rows.map((v) => {
                  const isDown = down.has(v.vehicle_id)
                  const status = fleetStatus(v, isDown)
                  const vBreakdown = breakdownByVehicle.get(v.vehicle_id)
                  const vAlert = alertByVehicle.get(v.vehicle_id)
                  // Same breakdown-over-alert priority fleetException() itself uses --
                  // whichever one won the exception chip is whose time fired the chip.
                  const exceptionTs = vBreakdown ? vBreakdown.opened_ts : vAlert?.opened_ts
                  return (
                    <div key={v.vehicle_id} className="group relative">
                      <VehicleCard
                        plate={v.vehicle_id}
                        status={status}
                        statusLabel={FLEET_STATUS_LABEL[status]}
                        place={v.route_code ? `Route ${v.route_code}` : 'no route on record'}
                        soc={v.soc_pct}
                        driver={v.driver_name}
                        speed={v.on_air ? (v.speed_kph != null ? Math.round(v.speed_kph) : null) : null}
                        elapsed={exceptionTs != null ? `${clock(exceptionTs)} · ${ago(exceptionTs)}` : undefined}
                        exception={fleetException(v, vBreakdown, vAlert)}
                        selected={selected === v.vehicle_id}
                        onClick={() => onSelectVehicle(v.vehicle_id)}
                      />
                      <button
                        type="button"
                        title="Play this duty"
                        onClick={(e) => { e.stopPropagation(); onPlayDuty(v.vehicle_id) }}
                        className="absolute right-2 top-2 hidden h-6 w-6 items-center justify-center rounded-full bg-[#0F172A]/90 text-white hover:bg-[#0F172A] group-hover:flex"
                      >
                        <Play size={10} />
                      </button>
                      <button
                        type="button"
                        title={isDown ? 'Update breakdown' : 'Mark breakdown'}
                        onClick={(e) => { e.stopPropagation(); onMarkBreakdown(v.vehicle_id) }}
                        className="absolute right-9 top-2 hidden h-6 w-6 items-center justify-center rounded-full bg-[#9F1239]/90 text-white hover:bg-[#9F1239] group-hover:flex"
                      >
                        <AlertOctagon size={10} />
                      </button>
                      {/* No NetradyneCameraButton here -- TUPL has no camera-vendor
                          integration confirmed yet, unlike MBMT (which shares a
                          Netradyne tenant with Ultratech). */}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto py-2">
              {roster.map((v) => (
                <button
                  key={v.vehicle_id}
                  type="button"
                  onClick={() => onSelectVehicle(v.vehicle_id)}
                  title={`${v.vehicle_id}${v.route_code ? ` · route ${v.route_code}` : ''}`}
                  className={clsx(
                    'mx-auto flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-[#F8FAFC]',
                    selected === v.vehicle_id && 'bg-[#EFF6FF]',
                  )}
                >
                  <span className={clsx(
                    'h-2 w-2 rounded-full',
                    down.has(v.vehicle_id) ? 'bg-[#F43F5E]'
                      : !v.on_air ? 'bg-[#CBD5E1]'
                        : v.moving ? 'bg-[#10B981]' : 'bg-[#F59E0B]',
                  )} />
                </button>
              ))}
            </div>
          )}
        </div>
      </Panel>
    </aside>
  )
}

function SocBar({ value }: { value: number | null }): JSX.Element | null {
  if (value == null) return null
  const tone = value < 20 ? 'bg-[#F43F5E]' : value < 40 ? 'bg-[#F59E0B]' : 'bg-[#10B981]'
  return (
    <div className="mt-3">
      <div className="flex justify-between text-[11px] text-[#64748B]">
        <span>SOC</span>
        <span className="font-mono font-semibold text-[#0F172A]">{Math.round(value)}%</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#EEF2F7]">
        <div className={clsx('h-full rounded-full', tone)} style={{ width: `${Math.max(2, Math.min(100, value))}%` }} />
      </div>
    </div>
  )
}

function QueueSkeleton(): JSX.Element {
  return (
    <div className="space-y-2 px-1 pt-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded-[12px] bg-[#EEF2F7]" />
      ))}
    </div>
  )
}
